import { useState } from 'react';

/**
 * Hook to manage local storage state
 * @param key - The key to store the value under in localStorage
 * @param defaultValue - The default value to use if the key doesn't exist in localStorage
 * @returns A stateful value and a function to update it that also updates localStorage
 */
function useLocalStorage<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      if (typeof window !== 'undefined') {
        const value = localStorage.getItem(key);

        if (value) {
          return JSON.parse(value) as T;
        } else {
          localStorage.setItem(key, JSON.stringify(defaultValue));
          return defaultValue;
        }
      } else {
        return defaultValue;
      }
    } catch (error) {
      console.error(error);
      return defaultValue;
    }
  });

  const setValue = (newValue: T): void => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(newValue));
      }
    } catch (error) {
      console.error(error);
    }
    setStoredValue(newValue);
  };

  return [storedValue, setValue];
}

export default useLocalStorage;
